import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import _ from 'lodash'
import { Modal, Form, Input, Header, Divider, Button, Confirm, Label, Icon } from 'semantic-ui-react'
import PropTypes from "prop-types";
import { GetHotelParameters } from '../../api/services/organization';
import { UpdateYields } from '../../api/services/pms';
import { onSuccess, onError, onWarning } from '../shared/messages'
import WeekDaysSelect from '../shared/WeekDaysSelect'
import { DatePicker } from '../shared/DatePicker';
import { useTextResources } from '../../language';
import { isMobileOnly } from 'mobile-device-detect';
import moment from 'moment';
import Slider from '../shared/Slider'
import { useLoader } from '../../hooks/useLoader';
import { dateFormat } from '../shared/constants';
import HotelsDropDown from '../shared/HotelsDropDown';

// Helper to get initial hotel ID from query params or prop
const getInitialHotelId = (propHotelID) => {
    const params = new URLSearchParams(location.search)
    const id = params.get('HotelId') || params.get('hotelId') || propHotelID || null
    return id ? Number(id) : null
}

const YieldPopup = ({ HotelID, onClose, controller, startOpen, onSave, initialDate, ...rest }) => {
    const text = useTextResources()
    const loader = useLoader()
    const [dates, setDates] = useState(() => initialDate ? [initialDate, initialDate] : [new Date(), new Date()])
    const [open, setOpen] = useState(startOpen);
    const [days, setDays] = useState(null);
    const [yieldLevel, setYieldLevel] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [params, setParams] = useState({ OnSave: onSave });
    const [saveYieldPopupActive, setSaveYieldPopupActive] = useState(false);
    const [closeAfterSave, setCloseAfterSave] = useState(false);
    const [currentBusinessDate, setCurrentBusinessDate] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState([])
    const [selectedHotel, setSelectedHotel] = useState(() => getInitialHotelId(HotelID)); // Initialized lazily once from query params or prop

    useEffect(() => {
        if (!initialDate) {
            setDates([currentBusinessDate, currentBusinessDate]);
        } else if (rest.open) {
            const clamped = moment.max(moment(initialDate), moment(currentBusinessDate).startOf("day")).toDate();
            setDates([clamped, clamped]);
        }
    }, [currentBusinessDate, initialDate, rest.open]);

    // Fetch business date when selected hotel changes
    useEffect(() => {
        const hotelToFetch = selectedHotel || HotelID
        if (!hotelToFetch) return

        let cancelled = false
        const fetchBusinessDate = async () => {
            try {
                const hotelParams = await GetHotelParameters({ ID: hotelToFetch })
                const hotels = hotelParams.CurrentHotel
                if (!cancelled && hotels?.length) {
                    setCurrentBusinessDate(new Date(hotels[0].HotelParameter.CurrentBusinessDate))
                }
            } catch (error) {
                console.error(error)
                error.status === 400 ? onWarning(error) : onError(error)
            }
        }
        fetchBusinessDate()

        return () => { cancelled = true }
    }, [selectedHotel, HotelID])

    const updateStartDate = useCallback((startDate) => {
        setDates(prevDates => [startDate, prevDates[1]])
    }, [])

    const updateEndDate = useCallback((endDate) => {
        setDates(prevDates => [prevDates[0], endDate])
    }, [])

    // Controller pattern for AngularJS integration
    useEffect(() => {
        if (controller) {
            window[controller] = async (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setParams(params)
                            updateStartDate(params.StartDate || new Date())
                            updateEndDate(params.EndDate || new Date())
                        } else {
                            // Re-read query params to match original behavior when opening without params
                            setSelectedHotel(getInitialHotelId(HotelID))
                        }
                        // Note: business date fetching happens automatically via selectedHotel effect
                        setOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }
    }, [controller, updateEndDate, updateStartDate, HotelID])

    useEffect(() => {
        if (dates[1] && moment(dates[1]).isBefore(moment(dates[0]))) {
            setDates(prevDates => [prevDates[0], prevDates[0]])
        }
    }, [dates])

    const checkIfValid = () => {
        const missingFields = []

        if (!selectedHotel) {
            missingFields.push(text("Hotel"))
        }

        if (missingFields.length === 0){
            return true;
        }

        onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
        return false;
    }

    const updateYields = async (removeExceptionsInDateRange) => {
        const isValid = checkIfValid();
        if (!isValid) {
            setSaveYieldPopupActive(false)
            return;
        }

        try {
            setIsSaving(true);
            setSaveYieldPopupActive(false);
            if (closeAfterSave) {
                closeAction();
            }
            loader(true)
            let weekDays = [0, 1, 2, 3, 4, 5, 6];
            let yieldDays = [];
            weekDays.forEach(wDay => {
                if (days.includes(wDay)) {
                    yieldDays.push(true)
                }
                else {
                    yieldDays.push(false);
                }
            });
            const thedays = _.reduce(yieldDays, (val, day, index) => {
                return val + (day ? String(index + 1) : "")
            }, "")
            if(selectedDates.length>0){
                for(let index = 0; index< selectedDates.length; index++){
                    await UpdateYields({
                        StartDate: selectedDates[index].startDate,
                        EndDate: selectedDates[index].endDate,
                        HotelId: selectedHotel,
                        Level: yieldLevel,
                        Days: thedays,
                        RemoveExceptionsInDateRange: removeExceptionsInDateRange
                    })
                }
            } else {
                await UpdateYields({
                    StartDate: dates[0],
                    EndDate: dates[1],
                    HotelId: selectedHotel,
                    Level: yieldLevel,
                    Days: thedays,
                    RemoveExceptionsInDateRange: removeExceptionsInDateRange
                })
            }

            if (params && params.OnSave) {
                params.OnSave();
            }
            setIsSaving(false);
            loader(false)
            onSuccess()
        } catch (error) {
            console.error(error)
            error.status === 400 ? onWarning(error) : onError(error)
        }
        loader(false)
        setIsSaving(false);
    }

    const resetFields = useCallback(() => {
        setYieldLevel('0');
        setSelectedDates([])
        const seedDate = initialDate ? moment.max(moment(initialDate), moment(currentBusinessDate).startOf("day")).toDate() : currentBusinessDate
        setDates([seedDate, seedDate])
        setSelectedHotel(HotelID)
    }, [currentBusinessDate, HotelID, initialDate])

    // Keep a ref to resetFields to avoid stale closures in the open effect
    const resetFieldsRef = useRef(resetFields)
    resetFieldsRef.current = resetFields

    const closeAction = () => {
        setOpen(false)
        resetFields()
        onClose?.()
    }

    useEffect(() => {
        if (!open) {
            resetFieldsRef.current();
        }
    }, [open]);

    const addDateToList = () => {
        if (selectedDates.some((d) => moment(d.startDate).isSame(dates[0], 'day') && moment(d.endDate).isSame(dates[1], 'day'))) {
            onWarning(text('WarningDuplicateDates'))
            return
        }

        setSelectedDates(selectedDates => selectedDates.concat(
            {
                startDate: dates[0],
                endDate: dates[1]
            }))
    }

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue)
        } else {
            const seedDate = initialDate ? moment.max(moment(initialDate), moment(currentBusinessDate).startOf("day")).toDate() : currentBusinessDate
            setDates([seedDate, seedDate])
        }
    }

    const onRemoveDateFromSelectedDates = async (index, e) => {
        if (e) {
            await setSelectedDates(selectedDates => selectedDates.filter((_, i) => i !== index))
        }
    }



    const memoDates = useMemo(() => {
        return selectedDates.length ? selectedDates : [{ startDate: dates[0], endDate: dates[1] }]
    }, [dates, selectedDates])

    return (
        <Modal size={isMobileOnly ? "fullscreen" : "tiny"} open={open} className={isMobileOnly ? "mobile" : ""} style={{ zIndex: 1000000 }} {...rest} >
            {!isMobileOnly && <Modal.Header>{text("YieldUpdate")}</Modal.Header>}
            <Modal.Content>
                <Form unstackable>
                    <Header style={{ color: "#4f849e" }}>Yield level applies to all products</Header>
                    <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                        {selectedDates.map((selectedDate, index) => (
                            <Label.Group key={index}>
                                <Label>
                                    {moment(selectedDate.startDate).format(dateFormat)}
                                    -
                                    {moment(selectedDate.endDate).format(dateFormat)}
                                    <Icon key={index} name={"delete"} onClick={onRemoveDateFromSelectedDates.bind(this, index)} />
                                </Label>
                            </Label.Group>
                        ))}
                    </Form.Group>
                    <Form.Group unstackable style={{ alignItems: "flex-end" }}>
                        <Form.Field width={12} style={{ display: "grid" }}>
                            <DatePicker
                                picktype='range'
                                onChange={handleDatesChanged}
                                label="Date Range"
                                value={dates}
                                minDate={moment(currentBusinessDate).startOf("day").toDate()}
                            />
                        </Form.Field>
                        <Form.Field widths={4}>
                            <Button style={{ position: "relative", marginTop: "22%" }}
                                primary
                                size="small"
                                onClick={addDateToList}>
                                Add date
                    </Button>
                    </Form.Field>
                </Form.Group>
                    <Divider hidden/>
                    <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={setDays} dates={memoDates}/>
                    <Form.Field label={text("Hotel")} required control={HotelsDropDown} onChange={setSelectedHotel} value={selectedHotel}/>
                    <div className="field" style={{ marginTop: "10px" }}>
                        <label>Yield Level</label>
                        <Input value={yieldLevel} type="number" min={0} max={100}
                            onChange={(e, data) => {
                                setYieldLevel(data.value);
                            }} action>
                            <input />
                            <div className="ui selection dropdown" style={{ padding: "0.78em", width: "100%" }}>
                                <Slider
                                    value={yieldLevel} color="blue"
                                    defaultMinValue={0}
                                    defaultMaxValue={100}
                                    step={1}
                                    onChange={setYieldLevel}
                                />
                            </div>
                        </Input>
                    </div>
                </Form>
                <Confirm
                    dimmer="inverted"
                    open={saveYieldPopupActive}
                    header="Yield Popup"
                    content={`Would you like new yield level to recalculate manually overridden prices?`}
                    confirmButton={{ content: "Yes", loading: isSaving }}
                    cancelButton={{ content: "No", loading: isSaving }}
                    onCancel={() => updateYields(false)}
                    onConfirm={() => updateYields(true)}
                />
            </Modal.Content>
            <Modal.Actions>
                {!isMobileOnly && <Button size={"small"} negative onClick={closeAction}>{text("Close")}</Button>}
                {!isMobileOnly && <Button size={"small"} onClick={resetFields}>{text("Reset")}</Button>}
                {!isMobileOnly && <Button size={"small"} loading={isSaving} positive onClick={() => { setSaveYieldPopupActive(true); setCloseAfterSave(true); }}>{text("Saveandclose")}</Button>}
                <Button size={"small"} loading={isSaving} positive onClick={() => setSaveYieldPopupActive(true)}>{text("Save")}</Button>
            </Modal.Actions>
        </Modal>
    )
}
YieldPopup.propTypes = {
    HotelID: PropTypes.number,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    controller: PropTypes.string,
    startOpen: PropTypes.bool,
    initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
}

export default YieldPopup
